class SultanInNumbers extends HTMLElement {
  constructor() {
    super();
    this.composition = null;
    this.quadrants = [];
    this.numberEls = [];
    this.activeIndex = -1;
    this.hasAnimated = false;
    this.hasCounted = false;
    this.isDesktop = false;
    this.reducedMotion = false;
    this.onResize = this.onResize.bind(this);
    this.onQuadrantEnter = this.onQuadrantEnter.bind(this);
    this.onQuadrantLeave = this.onQuadrantLeave.bind(this);
    this.onQuadrantClick = this.onQuadrantClick.bind(this);
    this.onQuadrantKeydown = this.onQuadrantKeydown.bind(this);
    this.onCompositionMove = this.onCompositionMove.bind(this);
    this.onCompositionLeave = this.onCompositionLeave.bind(this);
  }

  connectedCallback() {
    this.composition = this.querySelector('.sin__composition');
    this.quadrants = Array.from(this.querySelectorAll('.sin__quadrant'));
    this.numberEls = Array.from(this.querySelectorAll('.sin__number'));
    if (!this.composition || !this.quadrants.length) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mediaQuery = window.matchMedia('(min-width: 990px)');
    this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.isDesktop = this.mediaQuery.matches;

    this.bindEvents();
    this.initObserver();

    if (this.reducedMotion) {
      this.classList.add('is-visible');
      this.setFinalNumbers();
    }

    this.mediaQuery.addEventListener('change', this.onResize);
  }

  disconnectedCallback() {
    this.mediaQuery?.removeEventListener('change', this.onResize);
    this.observer?.disconnect();
    this.composition?.removeEventListener('pointermove', this.onCompositionMove);
    this.composition?.removeEventListener('pointerleave', this.onCompositionLeave);
    this.quadrants.forEach((quadrant) => {
      quadrant.removeEventListener('pointerenter', this.onQuadrantEnter);
      quadrant.removeEventListener('pointerleave', this.onQuadrantLeave);
      quadrant.removeEventListener('click', this.onQuadrantClick);
      quadrant.removeEventListener('keydown', this.onQuadrantKeydown);
    });
  }

  bindEvents() {
    this.quadrants.forEach((quadrant, index) => {
      quadrant.dataset.index = String(index);
      quadrant.addEventListener('pointerenter', this.onQuadrantEnter);
      quadrant.addEventListener('pointerleave', this.onQuadrantLeave);
      quadrant.addEventListener('click', this.onQuadrantClick);
      quadrant.addEventListener('keydown', this.onQuadrantKeydown);
    });

    if (this.isDesktop && this.hoverMedia.matches && !this.reducedMotion) {
      this.composition.addEventListener('pointermove', this.onCompositionMove);
      this.composition.addEventListener('pointerleave', this.onCompositionLeave);
    }
  }

  onResize() {
    const wasDesktop = this.isDesktop;
    this.isDesktop = this.mediaQuery.matches;

    if (wasDesktop !== this.isDesktop) {
      if (this.isDesktop && this.hoverMedia.matches && !this.reducedMotion) {
        this.composition.addEventListener('pointermove', this.onCompositionMove);
        this.composition.addEventListener('pointerleave', this.onCompositionLeave);
      } else {
        this.composition.removeEventListener('pointermove', this.onCompositionMove);
        this.composition.removeEventListener('pointerleave', this.onCompositionLeave);
        this.composition.style.removeProperty('--sin-parallax-x');
        this.composition.style.removeProperty('--sin-parallax-y');
      }

      if (!this.isDesktop && this.activeIndex === -1) {
        this.setActive(-1);
      }
    }
  }

  initObserver() {
    if (this.reducedMotion) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.hasAnimated = true;
            this.classList.add('is-visible');
            window.setTimeout(() => this.startCountUp(), 520);
            this.observer.disconnect();
          }
        });
      },
      { threshold: 0.22 }
    );

    this.observer.observe(this);
  }

  setActive(index) {
    this.activeIndex = index;
    this.composition.dataset.active = String(index);
    this.classList.toggle('is-interacting', index >= 0);

    this.quadrants.forEach((quadrant, i) => {
      const isActive = i === index;
      quadrant.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
  }

  onQuadrantEnter(event) {
    if (!this.isDesktop || !this.hoverMedia.matches) return;
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (!Number.isNaN(index)) {
      this.setActive(index);
    }
  }

  onQuadrantLeave(event) {
    if (!this.isDesktop || !this.hoverMedia.matches) return;
    const related = event.relatedTarget;
    if (related && this.composition.contains(related)) return;
    this.setActive(-1);
  }

  onQuadrantClick(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (Number.isNaN(index)) return;

    if (this.isDesktop && this.hoverMedia.matches) return;

    if (this.activeIndex === index) {
      this.setActive(-1);
    } else {
      this.setActive(index);
    }
  }

  onQuadrantKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (Number.isNaN(index)) return;

    if (this.activeIndex === index && !this.isDesktop) {
      this.setActive(-1);
    } else {
      this.setActive(index);
    }
  }

  onCompositionMove(event) {
    if (this.reducedMotion) return;
    const rect = this.composition.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    const px = Math.max(-1, Math.min(1, x)) * 5;
    const py = Math.max(-1, Math.min(1, y)) * 5;
    this.composition.style.setProperty('--sin-parallax-x', `${px}px`);
    this.composition.style.setProperty('--sin-parallax-y', `${py}px`);
  }

  onCompositionLeave() {
    this.composition.style.setProperty('--sin-parallax-x', '0px');
    this.composition.style.setProperty('--sin-parallax-y', '0px');
  }

  formatNumber(value) {
    return new Intl.NumberFormat('en-US').format(value);
  }

  setFinalNumbers() {
    this.numberEls.forEach((el) => {
      const target = parseInt(el.dataset.target, 10) || 0;
      const valueEl = el.querySelector('.sin__number-value');
      if (valueEl) valueEl.textContent = this.formatNumber(target);
    });
    this.hasCounted = true;
  }

  startCountUp() {
    if (this.hasCounted || this.reducedMotion) {
      this.setFinalNumbers();
      return;
    }

    this.hasCounted = true;
    const duration = 1400;
    const start = performance.now();

    const targets = this.numberEls.map((el) => ({
      el,
      valueEl: el.querySelector('.sin__number-value'),
      target: parseInt(el.dataset.target, 10) || 0
    }));

    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);

      targets.forEach(({ valueEl, target }) => {
        if (!valueEl) return;
        const current = Math.round(target * eased);
        valueEl.textContent = this.formatNumber(current);
      });

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }
}

if (!customElements.get('sultan-in-numbers')) {
  customElements.define('sultan-in-numbers', SultanInNumbers);
}
